import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import { AvailabilityDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomState } from "@tggstore/Store/Reducers";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
}

const mapStateToProps = (state: CustomState) => {
    const { isAuthenticated, rememberMe, isGuest } = getSession(state);
    const userIsAuthenticated = (isAuthenticated || rememberMe) && !isGuest;
    const defaultWarehouse = state.customDefaultWarehouse.warehouse?.id || "";
    const wareHouseList = state.customWareHouseList;
    const cart = getCurrentCartState(state).value;
    const productProperties = state.customProductCategories.productProperties;
    return {
        productSettings: getSettingsCollection(state).productSettings,
        userIsAuthenticated,
        defaultWarehouse,
        wareHouseList,
        isProductDetailPage: getCurrentPage(state).type === "ProductDetailsPage",
        isCartPage: getCurrentPage(state).type === "CartPage",
        cart,
        productProperties,
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProductContext;

export interface CustomProductWarehouseStyles {
    wrapper?: InjectableCss;
    selectedWarehouseLabelText?: TypographyPresentationProps;
    selectedWarehouseValueText?: TypographyPresentationProps;
    selectedWarehouseWrapper?: InjectableCss;
    inStockLabelText?: TypographyPresentationProps;
    inStockValueText?: TypographyPresentationProps;
    inStockLabelWrapper?: InjectableCss;
    totalStockLabelWrapper?: InjectableCss;
    totalStockValueText?: TypographyPresentationProps;
}

export const customProductWarehouseStyles: CustomProductWarehouseStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    selectedWarehouseWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            margin-top: 10px;
            flex-direction: column;
        `,
    },
    selectedWarehouseLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    selectedWarehouseValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    inStockLabelWrapper: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    inStockLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    inStockValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            padding-left: 5px;
            ${wrapInContainerStyles}
        `,
    },
    totalStockValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            padding-left: 5px;
            ${wrapInContainerStyles}

            @media (max-width: 768px) {
                padding-left: 0;
            }
        `,
    },
};

const styles = customProductWarehouseStyles;

const CustomProductWarehouse = ({
    productContext,
    userIsAuthenticated,
    defaultWarehouse,
    wareHouseList,
    isCartPage,
    productProperties,    
    productId,
    availability,
}: Props) => {
    const { inventory, unitOfMeasure } = productContext?.productInfo || {};
    const [warehouse, setWarehouse] = useState("");
    const [warehouseStock, setWarehouseStock] = useState(0);

    if (!userIsAuthenticated) {
        return null;
    }

    useEffect(() => {
        if (wareHouseList && defaultWarehouse) {
            const { warehouses } = wareHouseList;
            const warehouse = warehouses.find(warehouse => warehouse.id === defaultWarehouse);
            if (warehouse) {
                setWarehouse(warehouse.description);
            }
        }
    }, [wareHouseList, defaultWarehouse]);

    useEffect(() => {
        if (inventory && unitOfMeasure && !isCartPage) {
            const availability =
                inventory?.inventoryAvailabilityDtos?.find(
                    o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
                )?.availability || undefined;
            if (availability && availability.message && availability.message !== "") {
                const availabilitySplit = availability.message.split(":");
                if (availabilitySplit.length > 1) {
                    const availabilityValue = parseInt(availabilitySplit[availabilitySplit.length - 1], 10);
                    if (!Number.isNaN(availabilityValue)) {
                        setWarehouseStock(availabilityValue);
                    }
                } else {
                    setWarehouseStock(0);
                }
            }
        }else if(isCartPage && productProperties.length > 0){    
                if (availability && availability.message && availability.message !== "") {
                    const availabilitySplit = availability.message.split(":");
                    if (availabilitySplit.length > 1) {
                        const availabilityValue = parseInt(availabilitySplit[availabilitySplit.length - 1], 10);
                        if (!Number.isNaN(availabilityValue)) {
                            setWarehouseStock(availabilityValue);
                        }
                    } else {
                        setWarehouseStock(0);
                    }
                }           
        }
    }, [inventory, unitOfMeasure,  productProperties,productId]);

    return (
        <StyledWrapper {...styles.wrapper}>
            {userIsAuthenticated && (
                <>
                    <StyledWrapper {...styles.selectedWarehouseWrapper}>
                        <Typography {...styles.selectedWarehouseLabelText}>
                            {translate("Selected Warehouse")}:
                        </Typography>
                        <Typography {...styles.selectedWarehouseValueText} data-test-selector="selectedWarehouse">
                            {warehouse}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.inStockLabelWrapper}>
                        <Typography {...styles.inStockLabelText}>{translate("In Stock")}:</Typography>
                        <Typography {...styles.inStockValueText} data-test-selector="inStock">
                            {warehouseStock}
                        </Typography>
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(CustomProductWarehouse));

// Verndale Custom Component : Ticket TGGMS-386
// Product Detail Page and Product List Page, to show
// the selected warehouse and stock availability for the selected warehouse.
